import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { getCampaignTimelineData } from "./schema/campaign-timeline/get-campaign-timeline-data";
import { createAbTest } from "./schema/concierge/create-ab-test";
import { endAbTest } from "./schema/concierge/end-ab-test";
import { getAbTest } from "./schema/concierge/get-ab-test";
import { getActiveTextConciergeMerchants } from "./schema/concierge/get-active-text-concierge-merchants";
import { getImessageChannelExperimentAggregate } from "./schema/concierge/get-imessage-channel-experiment-aggregate";
import { getIMessageChannelExperimentAnalytics } from "./schema/concierge/get-imessage-channel-experiment-analytics";
import { getTextConciergeAnalytics } from "./schema/concierge/get-text-concierge-analytics";
import { getTextConciergeFleetAnalytics } from "./schema/concierge/get-text-concierge-fleet-analytics";
import { listAbTests } from "./schema/concierge/list-ab-tests";
import { claimConversation } from "./schema/conversations/claim-conversation";
import { getConversationById } from "./schema/conversations/get-conversation-by-id";
import { getConversationLogs } from "./schema/conversations/get-conversation-logs";
import { getConversationMessages } from "./schema/conversations/get-conversation-messages";
import { getQueueConversations } from "./schema/conversations/get-queue-conversations";
import { getQueueCount } from "./schema/conversations/get-queue-count";
import { hasDiscountConfigured } from "./schema/conversations/has-discount-configured";
import { releaseConversationClaim } from "./schema/conversations/release-conversation-claim";
import { debugEmailTemplate } from "./schema/email-templates/debug-email-template";
import { createMarketingAgentPrompt } from "./schema/marketing-agent-prompt/create-marketing-agent-prompt";
import { getAllMarketingAgentPrompts } from "./schema/marketing-agent-prompt/get-all-marketing-agent-prompts";
import { updateMarketingAgentPrompt } from "./schema/marketing-agent-prompt/update-marketing-agent-prompt";
import { getActiveMarketingAgentSplits } from "./schema/marketing-agent/get-active-marketing-agent-splits";
import { getMarketingAgentChannelDistribution } from "./schema/marketing-agent/get-marketing-agent-channel-distribution";
import { getMarketingAgentDecisionDetails } from "./schema/marketing-agent/get-marketing-agent-decision-details";
import { getMarketingAgentDecisionTrends } from "./schema/marketing-agent/get-marketing-agent-decision-trends";
import { getMarketingAgentDecisions } from "./schema/marketing-agent/get-marketing-agent-decisions";
import { getMarketingAgentKeyMetrics } from "./schema/marketing-agent/get-marketing-agent-key-metrics";
import { getMarketingAgentLlmCostMetrics } from "./schema/marketing-agent/get-marketing-agent-llm-cost-metrics";
import { getMarketingAgentLlmCostTrends } from "./schema/marketing-agent/get-marketing-agent-llm-cost-trends";
import { getMarketingAgentPerformanceMetrics } from "./schema/marketing-agent/get-marketing-agent-performance-metrics";
import { getMarketingAgentSplitTestMessageStats } from "./schema/marketing-agent/get-marketing-agent-split-test-message-stats";
import { getMarketingAgentSplitTestRevenueChart } from "./schema/marketing-agent/get-marketing-agent-split-test-revenue-chart";
import { getMarketingAgentSplitTestRevenueStats } from "./schema/marketing-agent/get-marketing-agent-split-test-revenue-stats";
import { getMarketingAgentSplitTestStatisticalSignificance } from "./schema/marketing-agent/get-marketing-agent-split-test-statistical-significance";
import { getMarketingAgentTagDistribution } from "./schema/marketing-agent/get-marketing-agent-tag-distribution";
import { getMarketingAgentTriggerEventDistribution } from "./schema/marketing-agent/get-marketing-agent-trigger-event-distribution";
import { getMarketingAgentWriterMessageTrends } from "./schema/marketing-agent/get-marketing-agent-writer-message-trends";
import { escalateToMerchantSupport } from "./schema/messages/escalate-to-merchant-support";
import { fetchDiscountInfo } from "./schema/messages/fetch-discount-info";
import { flagDraftMessage } from "./schema/messages/flag-draft-message";
import { getReplyToEmail } from "./schema/messages/get-reply-to-email";
import { regenerateDraftMessage } from "./schema/messages/regenerate-draft-message";
import {
  adminSendDraftMessage,
  contractorSendDraftMessage,
} from "./schema/messages/send-draft-message";
import {
  adminSendManualMessage,
  contractorSendManualMessage,
} from "./schema/messages/send-manual-message";
import { skipDraftMessage } from "./schema/messages/skip-draft-message";
import { alertRecoverSlack } from "./schema/recover/alert-recover-slack";
import { getRecoverAnalytics } from "./schema/recover/get-recover-analytics";
import { getRecoverAutomationMetrics } from "./schema/recover/get-recover-automation-metrics";
import { getRecoverConversationReturn } from "./schema/recover/get-recover-conversation-return";
import { getRecoverDailyMetrics } from "./schema/recover/get-recover-daily-metrics";
import { getRecoverMerchantNotes } from "./schema/recover/get-recover-merchant-notes";
import { getRecoverTemplates } from "./schema/recover/get-recover-templates";
import { searchRecoverTeams } from "./schema/recover/search-recover-teams";
import { unsubscribeRecoverForPhoneNumber } from "./schema/recover/unsubscribe-recover-for-phone-number";
import { getSampleTrackingCodes } from "./schema/shipment-tracking/get-sample-tracking-codes";
import { getTeamCarrierDistribution } from "./schema/shipment-tracking/get-team-carrier-distribution";
import { lookupTracking } from "./schema/shipment-tracking/lookup-tracking";
import { testTrackingSamples } from "./schema/shipment-tracking/test-tracking-samples";
import { getTeamSignupForms } from "./schema/signups/get-team-signup-forms";
import { getTeamsWithSignupForms } from "./schema/signups/get-teams-with-signup-forms";
import { migrateSignupForm } from "./schema/signups/migrate-signup-form";
import { setSpotlightsEnabled } from "./schema/signups/set-spotlights-enabled";

const marketingAgentPromptSchema = {
  createMarketingAgentPrompt,
  updateMarketingAgentPrompt,
  getAllMarketingAgentPrompts,
} as const;

const campaignTimelineSchema = { getCampaignTimelineData } as const;

const conciergeSchema = {
  getActiveTextConciergeMerchants,
  getTextConciergeAnalytics,
  getTextConciergeFleetAnalytics,
  getIMessageChannelExperimentAnalytics,
  getImessageChannelExperimentAggregate,
  listAbTests,
  getAbTest,
  createAbTest,
  endAbTest,
} as const;

const conversationsSchema = {
  claimConversation,
  getConversationById,
  getQueueConversations,
  getQueueCount,
  getConversationLogs,
  getConversationMessages,
  hasDiscountConfigured,
  releaseConversationClaim,
} as const;

const messagesSchema = {
  escalateToMerchantSupport,
  fetchDiscountInfo,
  getReplyToEmail,
  adminSendDraftMessage,
  contractorSendDraftMessage,
  adminSendManualMessage,
  contractorSendManualMessage,
  flagDraftMessage,
  skipDraftMessage,
  regenerateDraftMessage,
} as const;

const recoverSchema = {
  alertRecoverSlack,
  getRecoverAnalytics,
  getRecoverAutomationMetrics,
  getRecoverMerchantNotes,
  getRecoverDailyMetrics,
  getRecoverTemplates,
  searchRecoverTeams,
  unsubscribeRecoverForPhoneNumber,
  getRecoverConversationReturn,
} as const;

const marketingAgentSplitsSchema = {
  getActiveMarketingAgentSplits,
  getMarketingAgentDecisionDetails,
  getMarketingAgentDecisions,
  getMarketingAgentKeyMetrics,
  getMarketingAgentDecisionTrends,
  getMarketingAgentTagDistribution,
  getMarketingAgentChannelDistribution,
  getMarketingAgentTriggerEventDistribution,
  getMarketingAgentWriterMessageTrends,
  getMarketingAgentLlmCostMetrics,
  getMarketingAgentLlmCostTrends,
  getMarketingAgentPerformanceMetrics,
  getMarketingAgentSplitTestRevenueStats,
  getMarketingAgentSplitTestMessageStats,
  getMarketingAgentSplitTestRevenueChart,
  getMarketingAgentSplitTestStatisticalSignificance,
} as const;

const emailTemplatesSchema = { debugEmailTemplate } as const;

const shipmentTrackingSchema = {
  getTeamCarrierDistribution,
  getSampleTrackingCodes,
  lookupTracking,
  testTrackingSamples,
} as const;

const signupsSchema = {
  getTeamsWithSignupForms,
  getTeamSignupForms,
  migrateSignupForm,
  setSpotlightsEnabled,
} as const;

export const rpcDefinition = {
  ...emailTemplatesSchema,
  ...marketingAgentPromptSchema,
  ...campaignTimelineSchema,
  ...conciergeSchema,
  ...conversationsSchema,
  ...messagesSchema,
  ...recoverSchema,
  ...marketingAgentSplitsSchema,
  ...shipmentTrackingSchema,
  ...signupsSchema,
};

export type AdminMarketingRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
